import { AlertTriangle, ArrowRight, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.casedb.co';

const TeamAssignmentReplacement = () => {
    const [users, setUsers] = useState([]);
    const [fromUserId, setFromUserId] = useState('');
    const [toUserId, setToUserId] = useState('');
    const [preview, setPreview] = useState(null);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const sortedUsers = useMemo(() => {
        return [...users].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [users]);

    const selectedFromUser = users.find(user => String(user.id) === String(fromUserId));
    const selectedToUser = users.find(user => String(user.id) === String(toUserId));
    const canPreview = fromUserId && toUserId && fromUserId !== toUserId && !previewing && !applying;
    const canApply = preview?.case_count > 0 && !previewing && !applying;

    const fetchUsers = async () => {
        setLoadingUsers(true);
        setError('');

        try {
            const response = await fetch(`${API_BASE}/users.php?no_access=true`, { credentials: 'include' });
            const data = await response.json();

            if (!response.ok || data.success === false) {
                throw new Error(data.message || data.error || 'Failed to load users');
            }

            setUsers(data.users || []);
        } catch (err) {
            console.error('Failed to fetch users:', err);
            setError(err.message || 'Failed to load users');
        } finally {
            setLoadingUsers(false);
        }
    };

    const runReplacementRequest = async (action) => {
        setError('');
        setSuccess('');

        if (!fromUserId || !toUserId) {
            setError('Select both a source user and replacement user.');
            return;
        }

        if (fromUserId === toUserId) {
            setError('Source and replacement users must be different.');
            return;
        }

        const isApply = action === 'apply';
        if (isApply) {
            const confirmed = window.confirm(
                `Replace ${selectedFromUser?.name || 'the selected user'} with ${selectedToUser?.name || 'the replacement user'} in ${preview?.case_count || 0} cases? This cannot be undone automatically.`
            );

            if (!confirmed) return;
        }

        if (isApply) {
            setApplying(true);
        } else {
            setPreviewing(true);
            setPreview(null);
        }

        try {
            const response = await fetch(`${API_BASE}/team_assignment_replacement.php`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action,
                    from_user_id: Number(fromUserId),
                    to_user_id: Number(toUserId)
                })
            });
            const data = await response.json();

            if (!response.ok || data.success === false) {
                throw new Error(data.message || data.error || 'Team assignment replacement failed');
            }

            if (isApply) {
                setSuccess(`Updated ${data.case_count} cases with ${data.replacement_count} team assignment replacements.`);
                setPreview(null);
            } else {
                setPreview(data);
            }
        } catch (err) {
            console.error('Team assignment replacement failed:', err);
            setError(err.message || 'Team assignment replacement failed');
        } finally {
            setPreviewing(false);
            setApplying(false);
        }
    };

    useEffect(() => {
        document.title = 'Team Reassignment | CaseDB';
        fetchUsers();
    }, []);

    useEffect(() => {
        setPreview(null);
        setSuccess('');
    }, [fromUserId, toUserId]);

    return (
        <div className='firm-container team-reassignment-container'>
            <div id="page-header" className='nm' style={{ justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '25px', justifyContent: 'center', alignItems: 'center' }}>
                    <h2>Team Reassignment</h2>
                    <span className='subtext'>
                        Replace one user with another anywhere they appear in case team assignments, including primary role slots and nested role arrays.
                    </span>
                </div>
                <button className='team-reassignment-secondary-button' type='button' onClick={fetchUsers} disabled={loadingUsers}>
                    {loadingUsers ? <Loader2 size={15} className='spin' /> : <RefreshCw size={15} />}
                    Refresh Users
                </button>
            </div>

            <div className='team-reassignment-card'>
                <div className='team-reassignment-form'>
                    <label className='team-reassignment-field'>
                        <span className='small-text'>Replace this user</span>
                        <select value={fromUserId} onChange={(event) => setFromUserId(event.target.value)} disabled={loadingUsers || applying}>
                            <option value=''>Select source user</option>
                            {sortedUsers.map(user => (
                                <option value={user.id} key={`from-${user.id}`}>
                                    {user.name || user.user || user.email} {user.access_level === 'no access' ? '(No Access)' : ''}
                                </option>
                            ))}
                        </select>
                    </label>

                    <div className='team-reassignment-arrow'>
                        <ArrowRight size={20} />
                    </div>

                    <label className='team-reassignment-field'>
                        <span className='small-text'>With this user</span>
                        <select value={toUserId} onChange={(event) => setToUserId(event.target.value)} disabled={loadingUsers || applying}>
                            <option value=''>Select replacement user</option>
                            {sortedUsers.map(user => (
                                <option value={user.id} key={`to-${user.id}`}>
                                    {user.name || user.user || user.email} {user.access_level === 'no access' ? '(No Access)' : ''}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>

                <div className='team-reassignment-actions'>
                    <button className='action alt' type='button' onClick={() => runReplacementRequest('preview')} disabled={!canPreview}>
                        {previewing ? <Loader2 size={16} className='spin' /> : null}
                        Preview Affected Cases
                    </button>
                    <button className='action' type='button' onClick={() => runReplacementRequest('apply')} disabled={!canApply}>
                        {applying ? <Loader2 size={16} className='spin' /> : null}
                        Apply Replacement
                    </button>
                </div>

                {error ? <div className='team-reassignment-message error'>{error}</div> : null}
                {success ? <div className='team-reassignment-message success'><CheckCircle2 size={16} />{success}</div> : null}
            </div>

            {preview ? (
                <div className='team-reassignment-card'>
                    <div className='team-reassignment-summary'>
                        <div>
                            <span className='team-reassignment-stat'>{preview.case_count}</span>
                            <span className='subtext'>Affected Cases</span>
                        </div>
                        <div>
                            <span className='team-reassignment-stat'>{preview.replacement_count}</span>
                            <span className='subtext'>Assignments Replaced</span>
                        </div>
                        <div>
                            <span className='team-reassignment-stat'>{preview.dedupe_count}</span>
                            <span className='subtext'>Duplicate Targets Removed</span>
                        </div>
                    </div>

                    <div className='team-reassignment-preview-header'>
                        <h3>Preview</h3>
                        <span className='subtext'>Showing {Math.min(preview.cases?.length || 0, 50)} of {preview.case_count} cases</span>
                    </div>

                    {(preview.cases || []).length > 0 ? (
                        <div className='team-reassignment-case-list'>
                            {(preview.cases || []).slice(0, 50).map(caseItem => (
                                <div className='team-reassignment-case-row' key={caseItem.id}>
                                    <div>
                                        <strong>{caseItem.case_name || `Case #${caseItem.id}`}</strong>
                                        <span className='subtext'>Case ID {caseItem.id}</span>
                                    </div>
                                    <span className='team-reassignment-pill'>{caseItem.replacement_count} replacements</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className='subtext'>No active cases currently reference this source user in team assignments.</p>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default TeamAssignmentReplacement;
